import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { REPORT_ROWS } from "@/lib/content";
import { SectionHeading } from "./Primitives";

const DOT = { attention: "bg-[#C67D32]", detected: "bg-forest", track: "bg-sec", ok: "bg-forest" };
const TXT = { attention: "text-[#8A5518]", detected: "text-ink", track: "text-sec", ok: "text-sec" };

const Status = ({ status, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap ${TXT[status]}`}>
    <span className={`h-2 w-2 rounded-full ${DOT[status]}`} aria-hidden /> {label}
  </span>
);

const Row = ({ r }) => (
  <article className="grid gap-4 md:grid-cols-12 py-6 border-t border-divider first:border-t-0 first:pt-0" data-testid={`report-row-${r.id}`}>
    <div className="md:col-span-4">
      <p className="font-medium text-ink">{r.measurement}</p>
      <p className="mt-1.5 mono text-sec">{r.method} · {r.location}</p>
      <div className="mt-2"><Status status={r.status} label={r.statusLabel} /></div>
    </div>
    <div className="md:col-span-2">
      <p className="font-mono text-xl text-ink leading-none">{r.result}</p>
      <p className="font-mono text-xs text-sec mt-1.5">{r.unit}</p>
      <p className="mono text-sec mt-2 leading-snug">{r.reference}</p>
    </div>
    <div className="md:col-span-3 text-sm leading-relaxed text-ink">{r.explanation}</div>
    <div className="md:col-span-3 text-sm leading-relaxed text-ink"><span className="mono text-sec block mb-1">Next step</span>{r.next}</div>
  </article>
);

export const ReportPreview = () => {
  const [all, setAll] = useState(false);
  const rows = all ? REPORT_ROWS : REPORT_ROWS.filter((r) => r.primary);
  return (
    <section className="bg-white py-16 sm:py-28 rule" id="results" data-testid="report-section">
      <div className="container-x">
        <SectionHeading
          title={<>Understand the findings.<br /> Know your next steps.</>}
          lead="Every measurement comes with its result and unit, the sampling location, a relevant reference, a plain-language explanation and a recommended next step. No scores, no blanket safe-or-unsafe labels."
          testId="report-heading"
        />
        <div className="mt-12 sm:mt-14 rounded-md border border-divider" data-testid="report-preview">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-divider px-5 sm:px-6 py-4">
            <div className="flex items-center gap-3 mono text-sec">
              <span className="font-bold uppercase tracking-[0.26em] text-ink">Unseen</span>
              <span aria-hidden>·</span>
              <span>Sample findings · UES-EX-01</span>
            </div>
            <span className="inline-flex w-fit items-center rounded border border-sec/60 px-2.5 py-1 mono font-medium text-ink" data-testid="report-illustrative-label">
              Illustrative report preview—not results from a real home.
            </span>
          </div>
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-5 sm:px-6 pt-4 mono uppercase tracking-[0.14em] text-sec">
            <span className="md:col-span-4">Measurement · method · location</span><span className="md:col-span-2">Result · reference</span><span className="md:col-span-3">What it means</span><span className="md:col-span-3">Recommended next step</span>
          </div>
          <div className="px-5 sm:px-6 py-5">{rows.map((r) => <Row key={r.id} r={r} />)}</div>
          <div className="border-t border-divider px-5 sm:px-6 py-4">
            <button type="button" onClick={() => setAll((v) => !v)} aria-expanded={all} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:underline underline-offset-4 min-h-[44px]" data-testid="report-toggle">
              {all ? "Show the three key findings" : `Show the full sample report (${REPORT_ROWS.length} measurements)`}
              <ChevronDown size={16} className={`transition-transform ${all ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink" data-testid="report-supporting">
          Your assessment includes a 45-minute conversation to walk through the findings and discuss practical next steps.
        </p>
      </div>
    </section>
  );
};
